import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, Link, Tooltip, Typography } from '@mui/material';
import { useUrbanZoning } from '../../../hooks/useUrbanZoning';
import { buildEdificabilidadDisplayRows } from '../../../utils/lotFootprintDisplay';
import {
  ZONING_DISPLAY_GROUP_LABELS,
  groupDisplayRows,
  mergeEdificabilidadAndZoningRows,
  normalizeDisplayValue,
  resolveDisplayRowLinkHref,
  resolveZoningDisplayRows,
} from '../../../utils/urbanZoningDisplayRows';
import { lotInfoRowOuterSx, LOT_INFO_ROW_INNER_SX, LOT_INFO_SECTION_BORDER, lotInfoFieldLabelLayoutSx, lotInfoFieldValueSx } from './formBlockUi';

/**
 * Temporal: ids a ocultar en modo legacy (canonical/detail).
 * Con display_rows del backend no aplica: el servidor ya omite filas vacías.
 */
const HIDDEN_LEGACY_DETAIL_IDS = new Set([
  // 'enlace_grafico',
  // 'retiro_le',
]);

const HIDE_EXTRA_PROPERTIES = false;

const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      textAlign: 'center',
      maxWidth: 300,
      whiteSpace: 'normal',
      fontSize: '12px',
      lineHeight: 1.4,
    },
  },
};

const copyToClipboard = async (value, onCopied) => {
  const text = String(value ?? '').trim();
  if (!text) return;

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  if (onCopied) onCopied();
};

const InfoMessage = ({ children }) => (
  <Box
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: 1.5,
      border: '1px solid rgba(255,255,255,0.10)',
      bgcolor: 'rgba(0,0,0,0.12)',
    }}
  >
    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.35 }}>
      {children}
    </Typography>
  </Box>
);

const CopyableValue = ({
  value,
  copied = false,
  onCopy,
  highlight = false,
  muted = false,
  noWrap = false,
}) => {
  const display = normalizeDisplayValue(value);
  const isCopyable = Boolean(onCopy) && display !== '';

  const content = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        gap: 0.75,
        maxWidth: '100%',
        cursor: isCopyable ? 'pointer' : 'default',
        borderRadius: 1,
        px: isCopyable ? 0.75 : 0,
        py: isCopyable ? 0.35 : 0,
        transition: 'background-color 0.15s ease',
        '&:hover': isCopyable ? { backgroundColor: 'rgba(255,255,255,0.06)' } : undefined,
        '&:active': isCopyable ? { backgroundColor: 'rgba(255,255,255,0.10)' } : undefined,
        userSelect: isCopyable ? 'none' : 'text',
      }}
      role={isCopyable ? 'button' : undefined}
      tabIndex={isCopyable ? 0 : undefined}
      onClick={isCopyable ? onCopy : undefined}
      onKeyDown={isCopyable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCopy();
        }
      } : undefined}
    >
      <Typography
        component="span"
        sx={lotInfoFieldValueSx({ muted, highlight, noWrap })}
      >
        {display || '\u00A0'}
      </Typography>
      {copied && (
        <Typography
          sx={{
            fontSize: '11px',
            color: '#4ade80',
            fontWeight: 600,
            lineHeight: 1.4,
            flexShrink: 0,
          }}
        >
          Copiado
        </Typography>
      )}
    </Box>
  );

  if (!isCopyable) return content;

  return (
    <Tooltip title={copied ? 'Copiado' : 'Click para copiar'} placement="left" arrow>
      {content}
    </Tooltip>
  );
};

const DetailRow = ({
  label,
  displayText,
  format = 'text',
  href = null,
  highlight = false,
  muted = false,
  tooltip = '',
  copyKey = '',
  copied = false,
  onMarkCopied,
  hideDivider = false,
}) => {
  const labelNode = (
    <Typography
      sx={{
        ...lotInfoFieldLabelLayoutSx(),
        cursor: tooltip ? 'help' : 'default',
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Box sx={lotInfoRowOuterSx(hideDivider)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          ...LOT_INFO_ROW_INNER_SX,
        }}
      >
        {tooltip ? (
          <Tooltip title={tooltip} placement="top" arrow slotProps={TOOLTIP_SLOT_PROPS}>
            {labelNode}
          </Tooltip>
        ) : labelNode}

        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
          {format === 'link' && href ? (
            <Tooltip title={href} placement="top" arrow>
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  ...lotInfoFieldValueSx({ highlight }),
                  color: '#60a5fa',
                }}
              >
                Ver aquí
              </Link>
            </Tooltip>
          ) : (
            <CopyableValue
              value={displayText}
              copied={copied}
              highlight={highlight}
              muted={muted}
              onCopy={() => copyToClipboard(displayText, () => onMarkCopied?.(copyKey || label))}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const GroupHeader = ({ label }) => (
  <Box sx={{ borderBottom: `1px solid ${LOT_INFO_SECTION_BORDER}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
    <Box sx={{ ...LOT_INFO_ROW_INNER_SX, py: 0.75 }}>
    <Typography
      sx={{
        fontSize: '10px',
        fontWeight: 700,
        color: 'rgba(255,255,255,0.45)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
    </Box>
  </Box>
);

const NormativaDetailsTable = ({
  edificabilidadRows,
  zoningData,
  zoningStatus,
  zoningErrorMessage = '',
  hasPerfil,
}) => {
  const [copiedField, setCopiedField] = useState(null);

  const markCopied = (key) => {
    if (!key) return;
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const linkContext = useMemo(() => ({
    properties: zoningData?.detail && typeof zoningData.detail === 'object'
      ? zoningData.detail
      : {},
    documentsBaseUrl: zoningData?.documents_base_url ?? null,
  }), [zoningData?.detail, zoningData?.documents_base_url]);

  const zoningRows = useMemo(() => {
    if (!zoningData) return [];
    return resolveZoningDisplayRows(zoningData, {
      hiddenIds: HIDDEN_LEGACY_DETAIL_IDS,
      includeExtraProperties: !HIDE_EXTRA_PROPERTIES,
    }).rows;
  }, [zoningData]);

  const rows = useMemo(
    () => mergeEdificabilidadAndZoningRows(edificabilidadRows, zoningRows),
    [edificabilidadRows, zoningRows],
  );

  const groupedRows = useMemo(() => groupDisplayRows(rows), [rows]);

  const showZoningLoading = hasPerfil && zoningStatus === 'loading';
  const showZoningUnavailable = !hasPerfil;
  const showZoningError = hasPerfil && zoningStatus === 'error';
  const showZoningEmpty = hasPerfil && zoningStatus === 'ready' && zoningRows.length === 0;

  let rowIndex = 0;
  const totalRows = rows.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {zoningData?.source_label && (
        <Box sx={{ ...LOT_INFO_ROW_INNER_SX, pt: 1.25, pb: 0.75 }}>
          <Typography sx={lotInfoFieldValueSx({ muted: true })}>
            {zoningData.source_label}
          </Typography>
        </Box>
      )}

      {groupedRows.map((group, groupIndex) => {
        const groupLabel = ZONING_DISPLAY_GROUP_LABELS[group.id];
        const showGroupHeader = groupLabel && group.rows.length > 0;

        return (
          <React.Fragment key={`${group.id}-${groupIndex}`}>
            {showGroupHeader && <GroupHeader label={groupLabel} />}

            {group.rows.map((row, rowIndexInGroup) => {
              rowIndex += 1;
              const href = resolveDisplayRowLinkHref(row, linkContext);
              // Índice en key: el BE puede repetir id/group (ej. referencia, retiros).
              const rowKey = `${group.id}-${groupIndex}-${row.id}-${rowIndexInGroup}`;

              return (
                <DetailRow
                  key={rowKey}
                  label={row.label}
                  displayText={row.displayText}
                  format={row.format}
                  href={href}
                  highlight={row.highlight}
                  muted={row.muted}
                  tooltip={row.tooltip || ''}
                  copyKey={rowKey}
                  copied={copiedField === rowKey}
                  onMarkCopied={markCopied}
                  hideDivider={rowIndex === totalRows && !showZoningLoading}
                />
              );
            })}
          </React.Fragment>
        );
      })}

      {showZoningLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 1.5,
            borderTop: rows.length > 0 ? `1px solid ${LOT_INFO_SECTION_BORDER}` : 'none',
          }}
        >
          <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.55)' }} />
        </Box>
      )}

      {showZoningUnavailable && (
        <Box sx={{ mt: 1.5, mb: 1.5, px: 2 }}>
          <InfoMessage>
            No hay información de zonificación disponible para este lote.
          </InfoMessage>
        </Box>
      )}

      {showZoningError && (
        <Box sx={{ mt: 1.5, px: 2 }}>
          <InfoMessage>
            {zoningErrorMessage || 'No pudimos cargar el detalle normativo para este perfil.'}
          </InfoMessage>
        </Box>
      )}

      {showZoningEmpty && (
        <Box sx={{ mt: 1.5, px: 2 }}>
          <InfoMessage>
            No hay detalle normativo disponible para este perfil.
          </InfoMessage>
        </Box>
      )}
    </Box>
  );
};

const UrbanZoningSection = ({
  builtFootprintM2 = null,
  maxFootprintM2 = null,
  maxBuildableM2 = null,
  zoningPerfil,
  blocked = false,
  blockedMessage = 'Iniciá sesión para ver la normativa urbana del lote.',
  hideTitle = false,
}) => {
  const {
    status,
    zoningData,
    errorMessage,
    hasPerfil,
  } = useUrbanZoning({ zoningPerfil, blocked });

  const edificabilidadRows = useMemo(
    () => buildEdificabilidadDisplayRows({
      builtFootprintM2,
      maxFootprintM2,
      maxBuildableM2,
    }),
    [builtFootprintM2, maxBuildableM2, maxFootprintM2],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {!hideTitle && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.40)',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Normativa urbana
        </Typography>
      )}

      {blocked ? (
        <InfoMessage>{blockedMessage}</InfoMessage>
      ) : (
        <NormativaDetailsTable
          edificabilidadRows={edificabilidadRows}
          zoningData={zoningData}
          zoningStatus={status}
          zoningErrorMessage={errorMessage}
          hasPerfil={hasPerfil}
        />
      )}
    </Box>
  );
};

export default UrbanZoningSection;
